import { Helmet } from 'react-helmet';
import { Button, Divider, Modal, Space, Tooltip, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  Company,
  CompanyReduced,
  ListCompaniesQuery,
} from '@/core/domain/enterprise/Companies/Companies.types';

import {
  useCompaniesReduced,
  useDeleteCompany,
} from '@/core/domain/enterprise/Companies/Companies.hooks';

import { PageProps } from '@/types/PageProps';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import { COLUMNS_WIDTH } from '@/utils/table';
import { useState } from 'react';
import CompaniesFilters from '@/components/Companies/CompaniesFilters';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';

import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { useDebounce } from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import StatusTag from '@/components/_common/StatusTag';
import { DefaultRouteTabs, Group } from '@/components/_common';
import useValidateRoutes from '@/hooks/useValidateRoutes';
import UserGuiding from '@/core/UserGuinding';

const CompanyListPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListCompaniesQuery>({
    active: true,
    order: 'ASC',
    ...convertParams<ListCompaniesQuery>({
      search: 'string',
      name: 'string',
      cnpj: 'string',
      companyType: 'string',
      order: 'string',
      active: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  const { data, isFetching, fetchNextPage, hasNextPage } = useCompaniesReduced({
    ...queryDebounce,
    isCompanyFilter: true,
    includeBlocked: true,
  });

  usePersistSearchParams(queryDebounce);

  const companies = flatInfiniteData(data);
  const companyPages = data?.pages?.at(-1);

  const deleteMutation = useDeleteCompany();

  const isMaxLimitCompany =
    (companyPages?.totalCompanies || 0) >= (companyPages?.maxCompanies || 0);

  const handleRemoveClick = (company: Company) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'companies.remove' }),
      content: `${intl.formatMessage({
        id: 'companies.remove.alert',
      })} ${company.name}`,
      onOk: async () => {
        await deleteMutation.mutateAsync(company.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<CompanyReduced> = [
    {
      title: intl.formatMessage({ id: 'companies.list.header.name' }),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: COLUMNS_WIDTH.default,
    },
    {
      title: intl.formatMessage({ id: 'companies.list.header.cnpj' }),
      dataIndex: 'cnpj',
      key: 'cnpj',
      ellipsis: true,
      width: COLUMNS_WIDTH.document,
      render: (value) => applyMask(value, cnpjMask),
    },
    {
      title: intl.formatMessage({ id: 'companies.list.header.situation' }),
      width: COLUMNS_WIDTH.small,
      render: (_, row) =>
        row.active ? (
          <StatusTag label="Ativo" color="green" />
        ) : row?.blocked ? (
          <Tooltip
            title={
              isMaxLimitCompany
                ? 'Limite máximo de empresas atingido. Para desbloquear entre em contato com o nosso time de suporte.'
                : undefined
            }
          >
            <StatusTag label="Bloqueado" color="red" />
          </Tooltip>
        ) : (
          <StatusTag label="Desativado" color="gray" />
        ),
    },
    {
      key: 'main',
      align: 'center',
      width: 50,
      render: (row) =>
        row.main && (
          <Tooltip title="Empresa matriz">
            <StatusTag label="Principal" color="green" />
          </Tooltip>
        ),
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      key: 'actions',
      width: 100,
      align: 'right',
      fixed: 'right',
      className: 'company-list-action',
      render: (row) => (
        <Space>
          <EditIcon
            redirect={`/app/registry/companies/${row.id}/general`}
            disabled={!hasPermissions([Authorities.REGISTER_COMPANY_VIEW])}
            iconProps={{
              onClickCapture: () => {
                UserGuiding.goToNextGuide?.();
              },
            }}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={!hasPermissions([Authorities.REGISTER_COMPANY_DELETE])}
          />
        </Space>
      ),
    },
  ];

  const filterRoutes = route.parent.routes.filter(
    ({ key }) => key === 'bankSlips' || key === 'list',
  );

  route.parent.routes = filterRoutes;

  const routeValidate = useValidateRoutes({
    route: route.parent,
    keys: ['bankSlips'],
  });

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={routeValidate}
        baseKey={route.parent.key}
        basePath={route.parent.path}
        extra={
          <Link to="/app/registry/companies/create">
            <Button
              disabled={!hasPermissions([Authorities.REGISTER_COMPANY_ADD])}
              block
              type="primary"
            >
              <FormattedMessage id="companies.searchBar.button" />
            </Button>
          </Link>
        }
      >
        <CompaniesFilters
          queryParams={queryParams}
          setQueryParams={setQueryParams}
        />

        {companyPages?.totalCompanies && companyPages?.maxCompanies && (
          <Group position="right">
            <Typography.Text>
              <strong>Empresas:</strong> {companyPages?.totalCompanies} /{' '}
              {companyPages?.maxCompanies}
            </Typography.Text>
          </Group>
        )}

        <InfiniteTable<CompanyReduced>
          columns={columns}
          loading={isFetching}
          dataSource={companies}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
        />
      </DefaultRouteTabs>
    </>
  );
};

export default CompanyListPage;
